import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  ConnectionObject,
  ConnectionObjectForm,
  ConnectionObjectFormV2
} from '@bo-client/shared/models/connectionObject';
import { ContactDataDto, ContactDataForm } from '@bo-client/shared/models/contactDataDto';
import { DocumentDto, UploadForm } from '@bo-client/shared/models/documentDto';
import {
  ConnectionObjectAdditionalInfoForm,
  ConnectionObjectAdditionalInfoModel
} from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { RegexToCheckEMail } from '@bo-client/shared/forms/forms.validators';
import { ConnectionChangeDto, ConnectionChangeForm } from '@bo-client/shared/models/connectionChange.dto';

@Injectable({
  providedIn: 'root'
})
export class CreateFormsService {
  public form = new FormGroup({}, { updateOn: 'submit' });

  constructor(private fb: FormBuilder) {}

  supplyAppointment(data: {
    supplyAppointment: any;
    supplyAppointmentWishDate: any;
    supplyAppointmentWishPartday: any;
  }) {
    return this.fb.group({
      date: [data.supplyAppointment],
      time: [''],
      supplyAppointmentWishDate: [data.supplyAppointmentWishDate],
      supplyAppointmentWishPartday: [data.supplyAppointmentWishPartday]
    });
  }

  createContactDataFormV2(data: ContactDataDto, required?: boolean): ContactDataForm {
    return this.fb.group({
      salutation: [data.salutation, required ? Validators.required : []],
      firstName: [data.firstName],
      lastName: [data.lastName],
      company: [data.company],
      companyAddition: [data.companyAddition],
      mail: [data.mail || '', required ? [Validators.required, Validators.pattern(RegexToCheckEMail)] : []],
      phone: [data.phone || '', required ? Validators.required : []],
      street: [data.street, required ? Validators.required : []],
      streetNo: [data.streetNo, required ? Validators.required : []],
      zipCode: [data.zipCode, required ? Validators.required : []],
      city: [data.city, required ? Validators.required : []]
    });
  }

  createContactDataForm(data: ContactDataDto, notRequired?: boolean) {
    if (!data) {
      data = {};
    }
    let disableFields = false;
    if (!data.firstName && !data.lastName && !data.company) {
      disableFields = true;
    }
    return this.fb.group({
      salutation: [data.salutation, notRequired == null || !notRequired ? Validators.required : []],
      firstName: [{ value: data.firstName, disabled: disableFields }],
      lastName: [{ value: data.lastName, disabled: disableFields }],
      company: [data.company],
      companyAddition: [data.companyAddition],
      mail: [
        data.mail,
        notRequired == null || !notRequired
          ? [Validators.required, Validators.pattern(RegexToCheckEMail)]
          : []
      ],
      phone: [data.phone, notRequired == null || !notRequired ? Validators.required : []],
      street: [data.street, notRequired == null || !notRequired ? Validators.required : []],
      streetNo: [data.streetNo, notRequired == null || !notRequired ? Validators.required : []],
      zipCode: [data.zipCode, notRequired == null || !notRequired ? Validators.required : []],
      city: [data.city, notRequired == null || !notRequired ? Validators.required : []]
    });
  }

  connectionObjectForm(data: ConnectionObject, uniqueCoId: string) {
    if (!data) {
      data = {};
    }
    let zipCity = '';
    if (data.zipCode) {
      zipCity = data.zipCode;
    }
    if (data.city) {
      zipCity = zipCity + ' / ' + data.city;
    }
    return this.fb.group({
      zipCodeCity: [zipCity],
      zipCode: [data.zipCode],
      city: [data.city],
      street: [data.street],
      streetNo: [data.streetNo],
      streetNoAddition: [data.streetNoAddition],
      uniqueCoId: [uniqueCoId],
      developmentAreaName: [data.developmentAreaName],
      gisHouseConnectionPoint: [data.gisHouseConnectionPoint],
      corridorNumber: [data.corridorNumber]
    });
  }

  connectionObjectFormWithValidator(data: ConnectionObject, uniqueCoId: string): ConnectionObjectForm {
    if (!data) {
      data = {};
    }
    let zipCity = '';
    if (data.zipCode) {
      zipCity = data.zipCode;
    }
    if (data.city) {
      zipCity = zipCity + ' / ' + data.city;
    }
    return this.fb.group({
      zipCodeCity: [zipCity, [Validators.required]],
      zipCode: [data.zipCode],
      city: [data.city],
      street: [data.street],
      streetNo: [data.streetNo],
      streetNoAddition: [data.streetNoAddition],
      uniqueCoId: [uniqueCoId],
      developmentAreaName: [data.developmentAreaName],
      gisHouseConnectionPoint: [data.gisHouseConnectionPoint],
      corridorNumber: [data.corridorNumber]
    });
  }

  connectionObjectFormWithValidatorV2(data: ConnectionObject): ConnectionObjectFormV2 {
    let zipCity: string = null;
    if (data.city) {
      zipCity = data.zipCode + ' / ' + data.city;
    }
    return this.fb.group({
      zipCodeCity: [zipCity],
      zipCode: [data.zipCode],
      city: [data.city],
      street: [data.street],
      streetNo: [data.streetNo],
      streetNoAddition: [data.streetNoAddition],
      developmentAreaName: [data.developmentAreaName],
      corridorNumber: [data.corridorNumber],
      gisId: [data.gisId],
      gisHouseConnectionPoint: [data.gisHouseConnectionPoint],
      orgUnitDesc: [data.orgUnitDesc],
      sapLocationId: [data.sapLocationId]
    });

    /**
     *
     */
  }

  connectionObjectAdditionalInfoForm(
    data: ConnectionObjectAdditionalInfoModel
  ): ConnectionObjectAdditionalInfoForm {
    return this.fb.group({
      isNewBuilding: [data.isNewBuilding],
      buildingType: [data.buildingType],
      numberOfFlats: [data.numberOfFlats],
      numberOfUsers: [data.numberOfUsers],
      numberOfOtherUsers: [data.numberOfOtherUsers],
      objectUsage: [data.objectUsage]
    });
  }

  createUploadForm(doc: DocumentDto): UploadForm {
    return this.fb.group({
      cokDocId: [doc.cokDocId],
      displayName: [doc.displayName],
      fileName: [doc.fileName],
      id: [doc.id],
      url: [doc.url],
      tag: [doc.tag]
    });
  }

  mustTrue(control) {
    const enteredValue = control.value;
    return !enteredValue ? { mustTrue: true } : null;
  }

  sortListById(list: any[]) {
    return list.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }

  public formConfirmation() {
    return this.fb.group({
      formConfirmation: ['', [Validators.required, this.mustTrue]]
    });
  }

  greaterThanZero(control) {
    const enteredValue = control.value;
    return !(enteredValue > 0) ? { greaterThanZero: true } : null;
  }

  lessThanValue(value): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const v: number = +control.value;
      if (isNaN(v)) {
        return { lessThanValue: true, requiredValue: value };
      }
      if (+value < v) {
        return { lessThanValue: true, requiredValue: value };
      }
      return null;
    };
  }

  createConnectionChangeForm(model: ConnectionChangeDto): ConnectionChangeForm {
    return this.fb.group({
      area: [model.area || null],
      areas: [model.areas || []],
      connectionChangeRequestType: [model.connectionChangeRequestType || null],
      connectionObject: this.connectionObjectFormWithValidatorV2(model.connectionObject || {}),
      connectionObjectAdditionalInfo: this.connectionObjectAdditionalInfoForm(
        model.connectionObjectAdditionalInfo || {}
      ),
      customer: this.createContactDataFormV2(model.customer || {}, true),
      invoiceContact: this.createContactDataFormV2(model.invoiceContact || {}, false),
      uploads: this.fb.array(model.uploads.map((doc) => this.createUploadForm(doc))),
      powerIncrease: [model.powerIncrease || (null as boolean)],
      moveServiceEntranceBox: [model.moveServiceEntranceBox || (null as boolean)],
      moveConnectionLine: [model.moveConnectionLine || (null as boolean)],
      isOwner: [model.isOwner || (null as boolean)],
      installedPower: [model.installedPower || (null as number)],
      isEmptyHouse: [model.isEmptyHouse || (null as boolean)],
      requiredPower: [model.requiredPower || (null as number)],
      meterNumber: [model.meterNumber || null],
      technicalComment: [model.technicalComment || null],
      fromDate: [model.fromDate ? new Date(model.fromDate) : null],
      toDate: [model.toDate ? new Date(model.toDate) : null],
      differingInvoiceContact: [!!model.invoiceContact?.salutation],
      installedFuse: [model.installedFuse || null],
      civilEngineering: [model.civilEngineering || null],
      externalId: [model.externalId || null],
      externalCustomerId: [model.externalCustomerId || null],
      shortDescription: [model.shortDescription || null],
      longDescription: [model.longDescription || null],
      lineChangeComment: [model.lineChangeComment || null],
      supplyAppointmentWishDate: [
        model.supplyAppointmentWishDate ? new Date(model.supplyAppointmentWishDate) : null
      ],
      lbm: [model.lbm || null],
      responsibleBuildingAuthority: [model.responsibleBuildingAuthority || null],
      executingCivilEngineeringPartner: [model.executingCivilEngineeringPartner || null],
      executionDate: [model.executionDate ? new Date(model.executionDate) : (null as Date)],
      inspectionAppointmentComment: [model.inspectionAppointmentComment || null]
    });
  }
}
